import { useState } from 'react';
import { Link } from 'react-router-dom';
import './HowItWorks.css';

function HowItWorks() {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const faqs = [
    {
      question: 'Do I keep the same token every round?',
      answer: 'No. Each new round creates its own unique token. When a round ends, that token is used for redemption. The next round will have a completely new token.',
    },
    {
      question: 'Can I sell before the round ends?',
      answer: 'Yes. While a round is active, you can buy and sell freely. Selling burns your round tokens and pays you from the reserve. Once the round finalizes, selling is no longer available and redemption opens.',
    },
    {
      question: 'What happens when the dump vault triggers?',
      answer: 'When the dump vault accumulates enough SOL from fees, that SOL is automatically moved into the active round reserve. This strengthens the backing without minting any extra tokens, which can improve the reserve per token ratio.',
    },
    {
      question: 'How is redemption calculated?',
      answer: 'Redemption is based on your share of the final token supply. If you hold 10% of all tokens at finalization, you can redeem 10% of the final reserve. Your tokens are burned when you redeem.',
    },
    {
      question: 'What happens if I do not redeem in time?',
      answer: 'After the claim window closes, the round can be marked as closed. If you miss the window, your tokens may no longer be redeemable. Always check the redeem page after a round ends.',
    },
    {
      question: 'Can I trade during final countdown?',
      answer: 'Yes. You can continue buying and selling during the final countdown as long as the countdown has not yet expired. Once the countdown finishes, the round finalizes and trading stops.',
    },
    {
      question: 'Is there more than one live round at once?',
      answer: 'No. SolFire runs one active round at a time. This keeps the experience simple and ensures all activity is focused on a single round.',
    },
  ];

  return (
    <section className="page-section secondary-page how-it-works-page">
      {/* HERO SECTION */}
      <div className="hiw-hero">
        <div className="hiw-hero__content">
          <p className="eyebrow">How It Works</p>
          <h1 className="hiw-hero__title">How SolFire Works</h1>
          <p className="hiw-hero__subtitle">
            A simple explanation of how live rounds, trading, reserve boosts, and redemption work.
          </p>
          <p className="hiw-hero__intro">
            SolFire is a live trading experience where each round has its own token. Users buy and sell during the round,
            fees accumulate and can boost the reserve, and when the round ends, holders redeem their share of the final pool.
            Everything happens on-chain with transparent mechanics.
          </p>
        </div>

        <div className="hiw-glance">
          <p className="kicker">At a Glance</p>
          <div className="hiw-glance__grid">
            <div className="hiw-glance-card">
              <div className="hiw-glance-card__icon">1</div>
              <p>One live round at a time</p>
            </div>
            <div className="hiw-glance-card">
              <div className="hiw-glance-card__icon">2</div>
              <p>Buy and sell during the round</p>
            </div>
            <div className="hiw-glance-card">
              <div className="hiw-glance-card__icon">3</div>
              <p>Reserve boosts strengthen backing</p>
            </div>
            <div className="hiw-glance-card">
              <div className="hiw-glance-card__icon">4</div>
              <p>Redeem after finalization</p>
            </div>
          </div>
        </div>
      </div>

      {/* STEP BY STEP JOURNEY */}
      <div className="hiw-section">
        <div className="hiw-section__header">
          <p className="eyebrow">The Journey</p>
          <h2>Step by Step: How a Round Works</h2>
          <p className="hiw-section__intro">
            Follow the flow from round start to redemption. Each step is designed to be simple and transparent.
          </p>
        </div>

        <div className="hiw-steps">
          <div className="hiw-step">
            <div className="hiw-step__number">1</div>
            <div className="hiw-step__content">
              <h3>A new round opens</h3>
              <p>
                Only one round is active at a time. Each new round gets its own unique token. When a round begins,
                users can start buying into it right away.
              </p>
            </div>
          </div>

          <div className="hiw-step">
            <div className="hiw-step__number">2</div>
            <div className="hiw-step__content">
              <h3>Users buy into the round</h3>
              <p>
                Buying mints round tokens directly to your wallet. Each buy is capped at 3 SOL. Part of your SOL goes into the round reserve,
                and part goes to fees that support the platform and the dump vault.
              </p>
            </div>
          </div>

          <div className="hiw-step">
            <div className="hiw-step__number">3</div>
            <div className="hiw-step__content">
              <h3>Users can sell while the round is live</h3>
              <p>
                Selling burns your round tokens and pays you from the round reserve. Fee logic still applies when you sell.
                You can freely buy and sell as long as the round is active.
              </p>
            </div>
          </div>

          <div className="hiw-step">
            <div className="hiw-step__number">4</div>
            <div className="hiw-step__content">
              <h3>The dump vault can boost the round</h3>
              <p>
                Part of the fees from buys and sells collects in a separate dump vault. Once enough SOL accumulates and the threshold is met,
                that SOL is pushed into the live round reserve. No extra tokens are minted when this happens. This can improve the reserve backing per token.
              </p>
            </div>
          </div>

          <div className="hiw-step">
            <div className="hiw-step__number">5</div>
            <div className="hiw-step__content">
              <h3>The round moves toward ending</h3>
              <p>
                Each round has a hard maximum timer of 1,260 seconds. Under certain conditions, the round can enter a final countdown of 180 seconds.
                Trading is still allowed while the round is live and the countdown has not expired.
              </p>
            </div>
          </div>

          <div className="hiw-step">
            <div className="hiw-step__number">6</div>
            <div className="hiw-step__content">
              <h3>The round finalizes and redemption opens</h3>
              <p>
                When the round ends, the final reserve and final token supply are snapshotted. Holders can redeem their tokens
                for their pro rata share of the final pool. After the redeem window ends, the round can be closed.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* WHAT YOUR MONEY IS DOING */}
      <div className="hiw-section">
        <div className="hiw-section__header">
          <p className="eyebrow">Fee Breakdown</p>
          <h2>What Your Money Is Doing</h2>
          <p className="hiw-section__intro">
            When you buy or sell, your SOL is split between the reserve, platform fees, and the dump vault.
          </p>
        </div>

        <div className="hiw-split-visual">
          <div className="hiw-split-card">
            <div className="hiw-split-card__icon hiw-split-card__icon--reserve">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Reserve</h3>
            <p>The largest portion goes into the round reserve. This is what backs your tokens and what you can sell into or redeem from.</p>
          </div>

          <div className="hiw-split-card">
            <div className="hiw-split-card__icon hiw-split-card__icon--platform">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M3 9H21" stroke="currentColor" strokeWidth="2"/>
                <path d="M9 21V9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h3>Platform Fee</h3>
            <p>A small portion supports the platform and development. This keeps SolFire running smoothly.</p>
          </div>

          <div className="hiw-split-card">
            <div className="hiw-split-card__icon hiw-split-card__icon--dump">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 12V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 14L12 18L8 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Dump Fee</h3>
            <p>Another portion goes into the dump vault. When this vault fills up, it boosts the active round reserve without minting new tokens.</p>
          </div>
        </div>
      </div>

      {/* WHY THE RESERVE BOOST MATTERS */}
      <div className="hiw-section hiw-section--highlight">
        <div className="hiw-section__header">
          <p className="eyebrow">Reserve Boost</p>
          <h2>Why the Reserve Boost Matters</h2>
          <p className="hiw-section__intro">
            The dump vault is one of the most important mechanics in SolFire. Here's how it works.
          </p>
        </div>

        <div className="hiw-boost-flow">
          <div className="hiw-boost-step">
            <div className="hiw-boost-step__number">1</div>
            <div className="hiw-boost-step__content">
              <h3>Fees Accumulate</h3>
              <p>Every buy and sell sends a portion of SOL into the dump vault. This happens automatically in the background.</p>
            </div>
          </div>

          <div className="hiw-boost-arrow">→</div>

          <div className="hiw-boost-step">
            <div className="hiw-boost-step__number">2</div>
            <div className="hiw-boost-step__content">
              <h3>Threshold Is Met</h3>
              <p>Once the dump vault has enough SOL, the protocol automatically triggers a transfer to the active round reserve.</p>
            </div>
          </div>

          <div className="hiw-boost-arrow">→</div>

          <div className="hiw-boost-step">
            <div className="hiw-boost-step__number">3</div>
            <div className="hiw-boost-step__content">
              <h3>Reserve Strengthens</h3>
              <p>The reserve grows without minting any new tokens. This can improve the backing per token, benefiting all holders.</p>
            </div>
          </div>
        </div>

        <div className="hiw-boost-callout">
          <p>
            <strong>Key Point:</strong> The reserve boost does not dilute token holders. It only adds SOL to the reserve,
            making each token potentially more valuable. No extra supply is created.
          </p>
        </div>
      </div>

      {/* ROUND LIFECYCLE */}
      <div className="hiw-section">
        <div className="hiw-section__header">
          <p className="eyebrow">Round States</p>
          <h2>Round Lifecycle</h2>
          <p className="hiw-section__intro">
            Every round moves through clear stages. Here's what each state means.
          </p>
        </div>

        <div className="hiw-lifecycle">
          <div className="hiw-lifecycle-card">
            <div className="hiw-lifecycle-card__badge hiw-lifecycle-card__badge--pending">Pending</div>
            <h3>Round is Preparing</h3>
            <p>The round is set up and ready to launch. Trading has not started yet.</p>
          </div>

          <div className="hiw-lifecycle-arrow">→</div>

          <div className="hiw-lifecycle-card">
            <div className="hiw-lifecycle-card__badge hiw-lifecycle-card__badge--active">Active</div>
            <h3>Trading is Live</h3>
            <p>The round is live. Users can buy and sell. This is where all the action happens.</p>
          </div>

          <div className="hiw-lifecycle-arrow">→</div>

          <div className="hiw-lifecycle-card">
            <div className="hiw-lifecycle-card__badge hiw-lifecycle-card__badge--countdown">Final Countdown</div>
            <h3>Round is Almost Over</h3>
            <p>The round is in its final moments. Trading is still allowed until the countdown expires.</p>
          </div>

          <div className="hiw-lifecycle-arrow">→</div>

          <div className="hiw-lifecycle-card">
            <div className="hiw-lifecycle-card__badge hiw-lifecycle-card__badge--redeeming">Redeeming</div>
            <h3>Trading Ended, Claims Open</h3>
            <p>The round has finalized. Holders can now redeem their tokens for their share of the reserve.</p>
          </div>

          <div className="hiw-lifecycle-arrow">→</div>

          <div className="hiw-lifecycle-card">
            <div className="hiw-lifecycle-card__badge hiw-lifecycle-card__badge--closed">Closed</div>
            <h3>Claim Window Ended</h3>
            <p>The redeem window has passed and the round is archived. Past rounds are shown for history.</p>
          </div>
        </div>
      </div>

      {/* REDEEM PAGE EXPLANATION */}
      <div className="hiw-section">
        <div className="hiw-section__header">
          <p className="eyebrow">Redeem Page</p>
          <h2>What to Expect on the Redeem Page</h2>
          <p className="hiw-section__intro">
            After a round ends, head to the Redeem page to claim your share.
          </p>
        </div>

        <div className="hiw-redeem-grid">
          <div className="hiw-redeem-card">
            <h3>Redeemable Rounds</h3>
            <p>
              If you hold tokens from a finalized round, you'll see it in the Redeemable tab.
              Click the redeem button to claim your pro rata share of the final reserve. Your tokens are burned when you redeem.
            </p>
          </div>

          <div className="hiw-redeem-card">
            <h3>Closed Rounds</h3>
            <p>
              Past rounds that have finished their claim window are shown in the Closed tab for history.
              These rounds are archived and are no longer redeemable.
            </p>
          </div>

          <div className="hiw-redeem-card">
            <h3>Admin Functions</h3>
            <p>
              The close button you might see on some rounds is an admin or protocol function, not a normal user action.
              Regular users focus on redeeming their tokens before the window closes.
            </p>
          </div>

          <div className="hiw-redeem-card">
            <h3>Fast Data Loading</h3>
            <p>
              Round metadata is surfaced from the backend database for speed and to reduce unnecessary chain queries.
              This makes the redeem experience faster and smoother.
            </p>
          </div>
        </div>
      </div>

      {/* IMPORTANT RULES */}
      <div className="hiw-section hiw-section--rules">
        <div className="hiw-section__header">
          <p className="eyebrow">Rules</p>
          <h2>Important Rules to Know</h2>
          <p className="hiw-section__intro">
            Keep these key points in mind when using SolFire.
          </p>
        </div>

        <div className="hiw-rules-grid">
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>One active round at a time</p>
          </div>
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>Max 3 SOL per buy</p>
          </div>
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>Each round has its own token</p>
          </div>
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>Buy and sell while round is live</p>
          </div>
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>Redemption opens after finalization</p>
          </div>
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>Claim before the window closes</p>
          </div>
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>Wallet connection required</p>
          </div>
          <div className="hiw-rule-card">
            <div className="hiw-rule-card__icon">✓</div>
            <p>All mechanics are on-chain</p>
          </div>
        </div>
      </div>

      {/* FAQ SECTION */}
      <div className="hiw-section">
        <div className="hiw-section__header">
          <p className="eyebrow">FAQ</p>
          <h2>Frequently Asked Questions</h2>
          <p className="hiw-section__intro">
            Quick answers to common questions about SolFire.
          </p>
        </div>

        <div className="hiw-faq">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`hiw-faq-item ${openFaqIndex === index ? 'hiw-faq-item--open' : ''}`}
            >
              <button
                className="hiw-faq-item__question"
                onClick={() => toggleFaq(index)}
              >
                <span>{faq.question}</span>
                <span className="hiw-faq-item__toggle">{openFaqIndex === index ? '−' : '+'}</span>
              </button>
              {openFaqIndex === index && (
                <div className="hiw-faq-item__answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* FINAL CTA */}
      <div className="hiw-cta">
        <div className="hiw-cta__content">
          <h2>Ready to Get Started?</h2>
          <p>Jump into the live round or check your redeemable tokens.</p>
        </div>
        <div className="hiw-cta__buttons">
          <Link to="/" className="hiw-cta__button hiw-cta__button--primary">
            View Live Round
          </Link>
          <Link to="/redeem" className="hiw-cta__button hiw-cta__button--secondary">
            Go to Redeem Page
          </Link>
        </div>
      </div>
    </section>
  );
}

export default HowItWorks;